<template>
  <div class="md:flex gap-twentyFive">
    <div class="md:flex-grow mb-lg min-w-0">
      <div class="md:flex mb-lg">
        <BulkSendSearch
          class="align-middle flex-grow md:mr-lg min-w-0"
          @onSearch="handleSearch"
          :disabled="!bulkSendEnabled"
        />
        <!-- This is temporary, we will use a link component that has been created in another branch -->
        <IconButton
          size="small"
          icon="plus"
          :disabled="!planHasFeature('bulk_send')"
          class="whitespace-nowrap w-full mt-sm md:mt-0 md:w-auto"
          @click="$router.push({ name: 'BulkSendsCreate' })"
        >
          {{ $t('functional.newBulkSend') }}
        </IconButton>
      </div>
      <component
        v-if="emptyStateComponent"
        :is="emptyStateComponent"
      />

      <BulkSendsOverviewCard
        v-for="bulkSend in bulkSendCardArray"
        @delete="handleDelete"
        @refreshList="refresh"
        :key="bulkSend.fingerprint"
        :bulkSend="bulkSend"
        :loading="isLoading"
      />

      <NumberedPaginator
        v-if="meta && results.length"
        class="mt-lg"
        :meta="meta"
        resource="bulk sends"
        page-data-ec="bulk_send_dashboard_page"
        @page="handlePagination"
      />
    </div>

    <div class="md:flex-none md:w-sideBar">
      <WhatIsBulkSendWidget />
      <HelpAndSupportWidget />
    </div>
    <DeleteDraftBulkSendModal
      :show="showDeleteDraftBulkSendModal"
      @close="showDeleteDraftBulkSendModal = false"
      @refreshList="refresh"
      :name="bulkSendToDelete.name"
      :fingerprint="bulkSendToDelete.fingerprint"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

import useList from '@/composables/list/list';
import { ListBulkSend } from '@/store/modules/bulkSend/models';

import IconButton from '@/components/library/buttons/IconButton.vue';
import BulkSendSearch from '@/components/ui/bulkSend/BulkSendSearch.vue';
import BulkSendsOverviewCard from '@/components/ui/bulkSend/BulkSendsOverviewCard.vue';
import NoBulkSendsEmptyState from '@/components/ui/bulkSend/NoBulkSendsEmptyState.vue';
import NoResultsBulkSendsEmptyState from '@/components/ui/bulkSend/NoResultsBulkSendsEmptyState.vue';
import BulkSendFeatureUnavailableEmptyStateSuperAdmin
  from '@/components/ui/bulkSend/BulkSendFeatureUnavailableEmptyStateSuperAdmin.vue';
import BulkSendFeatureUnavailableEmptyStateNonSuperAdmin
  from '@/components/ui/bulkSend/BulkSendFeatureUnavailableEmptyStateNonSuperAdmin.vue';
import HelpAndSupportWidget from '@/components/ui/bulkSend/HelpAndSupportWidget.vue';
import WhatIsBulkSendWidget from '@/components/ui/bulkSend/WhatIsBulkSendWidget.vue';
import NumberedPaginator from '@/components/ui/pagination/Paginator.vue';
import DeleteDraftBulkSendModal from '@/components/ui/bulkSend/DeleteDraftBulkSendModal.vue';


export default {
  name: 'IndexPage',
  components: {
    DeleteDraftBulkSendModal,
    NumberedPaginator,
    IconButton,
    BulkSendSearch,
    BulkSendsOverviewCard,
    HelpAndSupportWidget,
    WhatIsBulkSendWidget,
    NoBulkSendsEmptyState,
    NoResultsBulkSendsEmptyState,
    BulkSendFeatureUnavailableEmptyStateSuperAdmin,
    BulkSendFeatureUnavailableEmptyStateNonSuperAdmin,
  },
  setup() {
    const [
      fetch,
      results,
      meta,
      isLoading,
      refresh,
    ] = useList('bulk-sends', ListBulkSend);

    return {
      fetch,
      results,
      meta,
      isLoading,
      refresh,
    };
  },
  created() {
    if (this.bulkSendEnabled) {
      // If the page is defined as a query string, use this to paginate results
      this.fetch({
        page: this.$route.query.page ? this.$route.query.page : 1,
      });
    }
  },
  computed: {
    ...mapGetters({
      planHasFeature: 'plan/planHasFeature',
      userHasRole: 'roles/userHasRole',
    }),
    bulkSendCardArray() {
      if (this.isLoading) {
        return new Array(4).fill({});
      }
      return this.results;
    },
    emptyStateComponent() {
      if (this.isLoading) {
        return null;
      }

      if (this.planHasFeature('bulk_send')) {
        if (this.results.length) {
          return null;
        }

        try {
          const parsedUrl = new URL(this.meta.links[1].url);
          // there is always a page param, so more than one means a search of some kind
          if ([...parsedUrl.searchParams].length > 1) {
            return NoResultsBulkSendsEmptyState;
          }
        } catch {
          // ah well not to worry
        }

        return NoBulkSendsEmptyState;
      }

      if (this.userHasRole('super-admin')) {
        return BulkSendFeatureUnavailableEmptyStateSuperAdmin;
      }

      return BulkSendFeatureUnavailableEmptyStateNonSuperAdmin;
    },
    bulkSendEnabled() {
      return this.planHasFeature('bulk_send');
    },
  },
  data() {
    return {
      bulkSendToDelete: {
        name: '',
        fingerprint: '',
      },
      showDeleteDraftBulkSendModal: false,
    };
  },
  methods: {
    handleDelete(fingerprint, name) {
      this.bulkSendToDelete = {
        fingerprint,
        name,
      };
      this.showDeleteDraftBulkSendModal = true;
    },
    handleSearch(query) {
      const filteredQuery = Object.fromEntries(Object.entries(query).filter(([_, v]) => v)); // eslint-disable-line
      this.fetch(filteredQuery);

      // If the page query string parameter is defined, set it to 1 on a new search
      if (this.$route.query.page && this.$route.query.page !== '1') {
        this.$router.replace({ query: { page: 1 } });
      }
    },
    handlePagination(url) {
      const parsedUrl = new URL(url);
      const page = parsedUrl.searchParams.get('page');

      this.fetch(Object.fromEntries(parsedUrl.searchParams));
      if (this.$route.query.page !== page) {
        this.$router.replace({ query: { page } });
      }
    },
  },
};
</script>
